import { useDispatch, useSelector } from "react-redux";
import { getLang, getModule, getPermissions } from "store/userSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { empty, errorMessage, formatDateTime, scrollInto, scrollToTop } from "helpers/common";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import AsyncSelect from "react-select/async";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { fetchRoles, getRoles } from "store/rolesSlice";
import { toast } from "helpers/toast";
import debounce from "helpers/debounce";
import randomString from "helpers/random";
import hasPermissionTo from "helpers/permission";
import { useOutletContext, useSearchParams } from "react-router-dom";
import iconApplicationAdmin from "assets/images/icons/role-application-admin.png";
import iconEvaluator from "assets/images/icons/role-evaluator.png";
import iconHr from "assets/images/icons/role-hr.png";
import iconItAdmin from "assets/images/icons/role-it-admin.png";
import iconReviewer from "assets/images/icons/role-reviewer.png";
import iconUserTime from "assets/images/icons/user-time-icon.png";
import { tableResize } from "helpers/tableResize";
import TextTruncate from "components/ui/TextTruncate";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import { Banner, Button, Field, Input, InputAffix, Modal, Select } from "components/ui";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { helpTextIcon } from "helpers/helpText";
import { getFixedFields } from "store/fieldsSlice";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { isDbModule } from "helpers/module";
import { roleIconSides } from "./usersActionSlots";
import ListPagination from "components/ui/ListPagination";
import { deeplinkSearchValue, deeplinkStaffId, deeplinkUserId } from "helpers/employeeDeeplink";

export const roleIcons = {
  'application-admin': iconApplicationAdmin,
  'evaluator': iconEvaluator,
  'it-admin': iconItAdmin,
  'reviewer': iconReviewer,
};

function visiblePermissionRoleNames(isDbModuleActive) {
  return isDbModuleActive
    ? ['it-admin', 'application-admin', 'module-admin-db', 'module-reviewer-db', 'hr-db']
    : ['it-admin', 'application-admin', 'module-admin-lob', 'module-reviewer-lob', 'hr-lob', 'hr'];
}

function roleLabel(roleName, editableTranslate) {
  if (roleName === 'module-admin-lob' || roleName === 'module-admin-db') {
    return editableTranslate('roles.module-admin', 'Modul-Administration');
  }

  if (roleName === 'hr-lob' || roleName === 'hr-db' || roleName === 'hr') {
    return editableTranslate('roles.hr', 'Personalverwaltung');
  }

  if (roleName === 'module-reviewer-lob' || roleName === 'module-reviewer-db') {
    return editableTranslate('roles.' + roleName, 'Modul-Rezensent*in (nur lesend)');
  }

  return editableTranslate('roles.' + roleName);
}

function ReadOnlyRoleChip({ roleName, editableTranslate }) {
  return <span className={`role-readonly-chip role-readonly-chip-${roleName} mr-2 mb-2`}>
    {roleLabel(roleName, editableTranslate)}
  </span>;
}

export function RoleIcon({ roleName, translate }) {
  if (roleName === 'it-admin' || roleName === 'application-admin') {
    return <span className={`role-icon role-icon-${roleName} mr-1`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.' + roleName)}>
      <img src={roleIcons[roleName]} alt="" />
    </span>;
  }

  if (roleName === 'module-admin-lob' || roleName === 'module-admin-db') {
    return <span className="role-icon role-icon-module-admin mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.module-admin', 'Modul-Administration')}>
      <i className="bi bi-shield text-normal"></i>
    </span>;
  }

  if (roleName === 'hr' || roleName === 'hr-lob' || roleName === 'hr-db') {
    return <span className="role-icon role-icon-hr mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.hr')}>
      <img src={iconHr} alt="" />
    </span>;
  }

  if (roleName === 'module-reviewer-lob' || roleName === 'module-reviewer-db') {
    return <span className="role-icon role-icon-module-reviewer mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.' + roleName, 'Modul-Rezensent*in (nur lesend)')}>
      <i className="bi bi-eye text-normal"></i>
    </span>;
  }

  return null;
}

export function InvisibleRoleIcon({ roleName }) {
  if (roleName === 'module-admin-lob' || roleName === 'module-admin-db') {
    return <span className="role-icon role-icon-module-admin mr-1 invisible">
      <i className="bi bi-shield text-normal"></i>
    </span>;
  }

  if (roleName === 'hr' || roleName === 'hr-lob' || roleName === 'hr-db') {
    return <span className="role-icon role-icon-hr mr-1 invisible">
      <img src={iconHr} alt="" />
    </span>;
  }

  return <span className="role-icon role-icon-it-admin mr-1 invisible">
    <img src={iconItAdmin} alt="" />
  </span>;
}

function GlobalAdminRoleIcon({ roles, translate, compact = false }) {
  const adminRoles = ['it-admin', 'application-admin'].filter(role => roles.includes(role));

  if (adminRoles.length === 0) {
    return compact ? null : <InvisibleRoleIcon roleName="it-admin" />;
  }

  const primaryRole = adminRoles.includes('it-admin') ? 'it-admin' : 'application-admin';
  const tooltip = adminRoles.map(role => translate('roles.' + role)).join(', ');

  return <span className={`role-icon role-icon-${primaryRole} mr-1`} data-tooltip-id="role-tooltip" data-tooltip-content={tooltip}>
    <img src={roleIcons[primaryRole]} alt="" />
  </span>;
}

/**
 * `compact` drops the invisible placeholders. They line the icons up column-wise across rows,
 * which is worth a fixed six slots on a form but not on the users listing, where most rows hold
 * one icon and the placeholders were costing ~150px of table width.
 */
export function ModuleAwareRoleIcons({ roles, isDbModuleActive, translate, allModules = false, compact = false }) {
  let moduleAdminRoles = ['module-admin-db'];
  if (allModules) {
    moduleAdminRoles.push('module-admin-lob');
  } else if (!isDbModuleActive) {
    moduleAdminRoles = ['module-admin-lob'];
  }
  const moduleAdminRole = moduleAdminRoles.find(role => roles.includes(role));

  let hrRoles = ['hr-db'];
  if (allModules) {
    hrRoles.push('hr-lob', 'hr');
  } else if (!isDbModuleActive) {
    hrRoles = ['hr-lob', 'hr'];
  }
  const hrRole = hrRoles.find(role => roles.includes(role));

  let moduleReviewerRoles = ['module-reviewer-db'];
  if (allModules) {
    moduleReviewerRoles.push('module-reviewer-lob');
  } else if (!isDbModuleActive) {
    moduleReviewerRoles = ['module-reviewer-lob'];
  }
  const moduleReviewerRole = moduleReviewerRoles.find(role => roles.includes(role));

  return <>
    <GlobalAdminRoleIcon roles={roles} translate={translate} compact={compact} />
    {
      moduleAdminRole
        ? <RoleIcon roleName={moduleAdminRole} translate={translate} />
        : (compact ? null : <InvisibleRoleIcon roleName={moduleAdminRoles[0]} />)
    }
    {
      hrRole
        ? <RoleIcon roleName={hrRole} translate={translate} />
        : (compact ? null : <InvisibleRoleIcon roleName={hrRoles[0]} />)
    }
    {
      moduleReviewerRole && <RoleIcon roleName={moduleReviewerRole} translate={translate} />
    }
  </>;
}

function GroupCategoryMenuOption({ option, uncategorized, value, setter }) {
  const isChecked = useMemo(() => {
    const filtered = value.filter(v => v.value === option.value);
    return filtered.length > 0 ? true : false;
  }, [option, value]);

  const handleChange = (checked) => {
    if (checked) {
      const newValues = [...value];
      newValues.push(option);
      setter(newValues);
    } else {
      setter(value.filter(v => v.value !== option.value));
    }
  };

  return <div className={`mb-2 ${uncategorized ? 'col-xl-4' : 'col-xl-6'}`}>
    <div className="form-check">
      <label className="form-check-label">
        <input className={`form-check-input`} type="checkbox" checked={isChecked} onChange={e => handleChange(e.target.checked)} data-testid={`group-category-option-${option.value}`} />
        <span className="ml-1">{option.label}</span>
      </label>
    </div>
  </div>
}

function GroupCategoryMenu({ category, value, setter }) {
  const [visible, setVisible] = useState(true);

  const handleToggle = (e) => {
    if (['INPUT', 'LABEL', 'SPAN'].includes(e.target.tagName)) return;
    setVisible(!visible);
  };

  const categoryValues = useMemo(() => {
    return category.options.map(o => o.value);
  }, [category]);

  const isAllChecked = useMemo(() => {
    const filtered = value.filter(v => categoryValues.includes(v.value));
    return filtered.length === categoryValues.length;
  }, [categoryValues, value]);

  const handleChange = (checked) => {
    if (checked) {
      const newValues = [...value];
      category.options.forEach(o => {
        let exists = false;
        for (const nv of newValues) {
          if (o.value === nv.value) {
            exists = true;
            break;
          }
        }
        if (!exists) {
          newValues.push(o);
        }
      });
      setter(newValues);
    } else {
      setter(value.filter(v => !categoryValues.includes(v.value)));
    }
  };

  return <div className={category?.uncategorized ? 'col-12' : 'col-xxl-6'}>
    <div className="group-category-div">
      <div data-testid={`group-category-toggle-${category.label}`} className="category-label-div" onClick={handleToggle}>
        <div className="form-check">
          <label className="form-check-label">
            <input className={`form-check-input`} type="checkbox" checked={isAllChecked} onChange={e => handleChange(e.target.checked)} data-testid={`group-category-all-${category.label}`} />
            <span className="ml-1">{category.label}</span>
          </label>
        </div>
        <i className={`bi ${visible ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
      </div>
      {
        visible && <div className="group-category-detail">
          <div className="row">
            {
              category.options.map((opt, j) => <GroupCategoryMenuOption key={j} option={opt} uncategorized={category?.uncategorized} value={value} setter={setter} />)
            }
          </div>
        </div>
      }
    </div>
  </div>
}

function GroupSelectorDropdown({ value, setter, options }) {

  const { editableTranslate } = useTranslation();

  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    const q = (search ?? '').trim().toLowerCase();
    if (!q) return options;

    return options
      .map(group => {
        const matchedChildren = (group.options || []).filter(child =>
          (child.label || '').toLowerCase().includes(q)
        );
        return matchedChildren.length ? { ...group, options: matchedChildren } : null;
      })
      .filter(Boolean);
  }, [options, search]);

  return <div className="user-groups-selector-menu">
    <div className="d-flex justify-content-between user-groups-selector-search-div">
      <div className="w-100 mr-2">
        <InputAffix suffix={<i className="bi bi-search ml-2"></i>}>
          <Input data-testid="search-input" type="text" placeholder="Suche" autoComplete="false" maxLength={255} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }} />
        </InputAffix>
      </div>
      {/* <button type="button" className="btn btn-outline-primary btn-icon"><i className="bi bi-filter bi-bold"></i></button> */}
    </div>
    <div className="row">
      {
        filteredOptions.map((cat, i) => <GroupCategoryMenu key={i} category={cat} value={value} setter={setter} />)
      }
      {
        filteredOptions.length === 0 && <div className="col" data-testid="empty-state">
          {editableTranslate('global.no_data')}
        </div>
      }
    </div>
  </div>
}

function GroupsSelector({ value, setter, role, roleOpened, setRoleOpened, readOnly = false }) {
  const handleClick = (e) => {
    if (readOnly) return;

    if (e?.target?.tagName === 'I' && e?.target?.className?.includes('bi-x')) return;

    setRoleOpened(roleOpened ? null : role);
  };

  const handleRemoveValue = (selected) => {
    setter(value.filter(val => val.value !== selected.value));
  };

  return (
    <div className="position-relative w-100">
      <div data-testid={`groups-selector-${role}`} className={`user-groups-selector-input peb-input-height ${roleOpened === role ? 'active' : ''}`} onClick={handleClick}>
        <div className="d-flex value-div-parent">
          {
            value && value.slice(0, 2).map((v, i) => <div key={i} className="value-div">
              <div className={`value-label ${role + '-bg'} ${readOnly ? 'readonly' : ''}`}>{v.label}</div>
              {
                !readOnly && <div data-testid={`remove-value-${v.value}`} className={`value-button ${role + '-bg'}`} onClick={() => handleRemoveValue(v)}><i className="bi bi-x bi-bold"></i></div>
              }
            </div>)
          }
          {
            (value && value.length > 2) && <div className="value-div">
              <div className={`value-label ${role + '-bg'} readonly`}>+{value.length - 2}</div>
            </div>
          }
        </div>
        {
          !readOnly && <div className="d-flex align-items-center">
            <span className="user-groups-selector-input-separator"></span>
            <i className="bi bi-chevron-down bi-bold"></i>
          </div>
        }
      </div>
    </div>
  );
};

function UserDetail({ user, groups, fetchUsers, isEditMode, setIsEditMode, isDbModuleActive, fixedFieldsObj = {} }) {
  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();

  const permissions = useSelector(getPermissions);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [groupEvaluating, setGroupEvaluating] = useState(user?.groups_evaluating?.map(g => ({ value: g.hash_id, label: g.name })) || []);
  const [groupEvaluatingOptions, setGroupEvaluatingOptions] = useState([]);

  const [groupSecondaryEvaluating, setGroupSecondaryEvaluating] = useState(user?.groups_secondary_evaluating?.map(g => ({ value: g.hash_id, label: g.name })) || []);
  const [groupSecondaryEvaluatingOptions, setGroupSecondaryEvaluatingOptions] = useState([]);

  const [groupReviewing, setGroupReviewing] = useState(user?.groups_reviewing?.map(g => ({ value: g.hash_id, label: g.name })) || []);
  const [groupReviewingOptions, setGroupReviewingOptions] = useState([]);

  const [roleOpened, setRoleOpened] = useState(null);

  const [showPassword, setShowPassword] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const readonlyPermissionRoles = useMemo(() => {
    const roles = user?.roles || [];
    return visiblePermissionRoleNames(isDbModuleActive).filter(role => roles.includes(role));
  }, [isDbModuleActive, user?.roles]);

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const schema = yup.object({
    first_name: yup.string().required().label(fixedFieldsObj?.first_name?.custom_name || translate('field.first_name')),
    last_name: yup.string().required().label(fixedFieldsObj?.last_name?.custom_name || translate('field.last_name')),
    employee_number: yup.string().notRequired().max(255).label(fixedFieldsObj?.employee_number?.custom_name || translate('field.employee_number')),
    password: yup.string().notRequired().label(translate('login_users.password')),
  });

  // A login user from before LIX-1809 has no employee record yet; its number can be added here.
  const canAddEmployeeNumber = !user?.employeeInfo;

  const { register, watch, control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isEditMode) {
      reset({
        first_name: user?.first_name,
        last_name: user?.last_name,
        employee_number: '',
        password: '',
      });
    }
  }, [isEditMode, user]);

  useEffect(() => {
    if (user && groups) {
      if (user?.groups_evaluating) {
        setGroupEvaluating(user.groups_evaluating.map(g => ({ value: g.hash_id, label: g.name })));
      }
      if (user?.groups_secondary_evaluating) {
        setGroupSecondaryEvaluating(user.groups_secondary_evaluating.map(g => ({ value: g.hash_id, label: g.name })));
      }
      if (user?.groups_reviewing) {
        setGroupReviewing(user.groups_reviewing.map(g => ({ value: g.hash_id, label: g.name })));
      }
    }

    const groupOptions = [];

    const categories = {};
    const uncategorized = [];

    groups?.forEach(g => {
      const obj = { value: g.hash_id, label: g.name };
      const cat = g?.category ? g.category.trim().toLowerCase() : null;

      if (cat) {
        if (!categories[cat]) categories[cat] = [];

        categories[cat].push(obj);
      } else {
        uncategorized.push(obj);
      }
    });

    const keys = Object.keys(categories).sort();
    keys.forEach(key => {
      groupOptions.push({
        label: key,
        options: categories[key]
      });
    });

    groupOptions.push({
      label: translate('login_users.uncategorized'),
      uncategorized: true,
      options: uncategorized
    });

    setGroupEvaluatingOptions(groupOptions);
    setGroupSecondaryEvaluatingOptions(groupOptions);
    setGroupReviewingOptions(groupOptions);
  }, [user, groups]);

  const handleRandomize = () => {
    const val = randomString(10);

    setValue('password', val);

    setShowPassword(true);
  };

  const checkGroups = () => {
    const evaluatingValues = new Set(groupEvaluating.map(ge => ge.value));

    if (isDbModuleActive) {
      return groupSecondaryEvaluating.filter(gse => evaluatingValues.has(gse.value)).map(gse => gse.label);
    } else {
      return groupReviewing.filter(gr => evaluatingValues.has(gr.value)).map(gr => gr.label);
    }
  };

  const handleSaveUser = async (values) => {
    setError(null);

    const checkedGroups = checkGroups();
    if (checkedGroups.length > 0) {
      setError(translate(isDbModuleActive ? 'state_employee.evaluate_same_group_error' : 'login_users.error_evaluating_reviewing_group')
        .replace('$X', '<b>' + checkedGroups.join(', ') + '</b>'));
      scrollInto('.employee-detail-div', false, -150);
      return;
    }

    setLoading(true);

    const formData = {
      evaluators: groupEvaluating.map(g => g.value),
      reviewers: groupReviewing.map(g => g.value),
      user_hash_id: user?.hash_id,
      ...values
    };

    if (!canAddEmployeeNumber || empty(formData.employee_number?.trim())) {
      delete formData.employee_number;
    }

    if (isDbModuleActive) {
      formData.secondary_evaluators = groupSecondaryEvaluating.map(g => g.value);
    }

    try {
      let result = null;
      result = await api.put(`/users`, formData);

      if (result.status === 200) {
        roleOpened && setRoleOpened(null);
        fetchUsers(false);
        toast.success(translate('form.success.saved'));
        setIsEditMode(false);
      }
    } catch (err) {
      const existingEmployee = err?.response?.data?.data?.existing_employee;
      setError(existingEmployee
        ? translate('message.error.employee_number_belongs_to_other_person', 'Die Personalnummer $Y gehört bereits zu $X.')
          .replace('$X', '<b>' + existingEmployee.name + '</b>')
          .replace('$Y', existingEmployee.employee_number)
        : errorMessage(err, 'save login user'));
      scrollInto('.employee-detail-div', false, -150);
      Sentry.captureException(err);
    }

    setLoading(false);
  };

  const handleUserDelete = async () => {
    if (!user) return;

    setError(null);
    setLoadingDelete(true);

    try {
      const { status } = await api.delete(`/users/${user?.hash_id}`);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        setShowDeleteModal(false);

        fetchUsers(false);
      }
    } catch (err) {
      setError(errorMessage(err, 'delete user'));
      scrollInto('.employee-detail-div', false, -150);
      Sentry.captureException(err);
    }

    setLoadingDelete(false);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setRoleOpened(null);
  };

  return (
    // create 3 columns layout
    <div className="employee-detail-div">
      {
        error && <div className="row">
          <div className="col-12 login-users-detail">
            <div className="employee-data-card bg-transparent no-border-all px-0 pt-0">
              <ErrorAlert error={error} isHtml={true} onClose={() => setError(null)} />
            </div>
          </div>

        </div>
      }
      <div className={`row flex-nowrap`}>
        {
          !isEditMode && <div className="col-12 d-flex login-users-detail">
            {/* create a 3 boxes in a row */}
            <div className="employee-data-card two-cols mr-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="mb-0">{editableTranslate('login_users.user')}</h4>
                <div>
                  <div className="text-nowrap">
                    <span className="v-middle" data-tooltip-id="last-login-tooltip">
                      <img src={iconUserTime} className="icon-1rem mr-1" /> {user?.last_login_at ? formatDateTime(user?.last_login_at) : '-'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="employee-data-card-div">
                <div className="card-setting-header d-flex justify-content-between align-items-center">
                  <span>{editableTranslate('login_users.basic_details', 'Grundlegende Details')}</span>
                </div>
                <hr className="mt-2" />
                <div className="card-setting-body mb-n3">
                  <div className="col-12 p-0">
                    <div className="row mb-3">
                      <div className="col-3 gray font-weight-600">{fixedFieldsObj?.first_name?.custom_name || editableTranslate('field.first_name')}</div>
                      <div className="col-9 font-weight-600">
                        <TextTruncate text={user?.first_name || ''} />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-3 gray font-weight-600">{fixedFieldsObj?.last_name?.custom_name || editableTranslate('field.last_name')}</div>
                      <div className="col-9 font-weight-600">
                        <TextTruncate text={user?.last_name || ''} />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-3 gray font-weight-600">{fixedFieldsObj?.email?.custom_name || editableTranslate('field.email')}</div>
                      <div className="col-9 font-weight-600">
                        <TextTruncate text={user?.email || ''} />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-3 gray font-weight-600">{fixedFieldsObj?.employee_number?.custom_name || editableTranslate('field.employee_number')}</div>
                      <div className="col-9 font-weight-600" data-testid="employee-number">
                        <TextTruncate text={user?.employeeInfo?.employee_number || ''} />
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-3 gray font-weight-600">{editableTranslate('login_users.role')}</div>
                      <div className="col-9 font-weight-600">
                        <div className="d-flex flex-wrap">
                          {
                            readonlyPermissionRoles.map(role => <ReadOnlyRoleChip key={role} roleName={role} editableTranslate={editableTranslate} />)
                          }
                          {
                            readonlyPermissionRoles.length === 0 && <span>-</span>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="employee-data-card two-cols">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="mb-0">{editableTranslate('login_users.groups', 'Gruppen')}</h4>
              </div>
              <div className="employee-data-card-div">
                <div className="card-setting-header">
                  <span>
                    <i className="bi bi-clipboard-check bi-bold text-big mr-1"></i> {editableTranslate(!isDbModuleActive ? 'roles.evaluator' : 'state_employee.first_evaluator')}
                  </span>
                </div>
                <hr className="mt-2" />
                <div className="card-setting-body mt-3">
                  {
                    groupEvaluating && groupEvaluating.map((v, i) => <span key={i} className="badge badge-pill badge-evaluator badge-wrap text-left mr-1">{v.label}</span>)
                  }
                  {
                    (!groupEvaluating || groupEvaluating.length < 1) && <span>-</span>
                  }
                </div>
              </div>

              {
                isDbModuleActive && <div className="employee-data-card-div">
                  <div className="card-setting-header">
                    <span>
                      <i className="bi bi-clipboard-check bi-bold text-big mr-1"></i> {editableTranslate('state_employee.second_evaluator')}
                    </span>
                  </div>
                  <hr className="mt-2" />
                  <div className="card-setting-body mt-3">
                    {
                      groupSecondaryEvaluating && groupSecondaryEvaluating.map((v, i) => <span key={i} className="badge badge-pill badge-secondary-evaluator badge-wrap text-left mr-1">{v.label}</span>)
                    }
                    {
                      (!groupSecondaryEvaluating || groupSecondaryEvaluating.length < 1) && <span>-</span>
                    }
                  </div>
                </div>
              }

              {
                !isDbModuleActive && <div className="employee-data-card-div">
                  <div className="card-setting-header">
                    <span>
                      <i className="bi bi-eye bi-bold text-big mr-1"></i> {editableTranslate('roles.reviewer')}
                    </span>
                  </div>
                  <hr className="mt-2" />
                  <div className="card-setting-body mt-3">
                    {
                      groupReviewing && groupReviewing.map((v, i) => <span key={i} className="badge badge-pill badge-reviewer badge-wrap text-left mr-1">{v.label}</span>)
                    }
                    {
                      (!groupReviewing || groupReviewing.length < 1) && <span>-</span>
                    }
                  </div>
                </div>
              }
            </div>
          </div>
        }
        {
          isEditMode && <div className="col-12 login-users-detail">
            <form className="employee-data-card" onSubmit={handleSubmit(handleSaveUser)}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="mb-0">{editableTranslate('global.edit')}</h4>
                <div>
                  <Button variant="secondary" onClick={() => handleCancelEdit()} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                  <Button type="submit" variant="primary" loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>
                </div>
              </div>
              <div className="d-flex">
                <div className="employee-data-card-div w-50 mr-4">
                  <div className="card-setting-header d-flex justify-content-between align-items-center">
                    <span>{editableTranslate('login_users.basic_details', 'Grundlegende Details')}</span>
                  </div>
                  <hr className="mt-2" />
                  <div className="card-setting-body mb-n3">
                    <div className="form-row">
                      <div className="form-group col-6">
                        <Field label={fixedFieldsObj?.first_name?.custom_name || editableTranslate('field.first_name')} error={errors?.first_name?.message}>
                          <Input type="text" error={!!errors?.first_name} maxLength={255} {...register('first_name')} data-testid="first-name-input" />
                        </Field>
                      </div>
                      <div className="form-group col-6">
                        <Field label={fixedFieldsObj?.last_name?.custom_name || editableTranslate('field.last_name')} error={errors?.last_name?.message}>
                          <Input type="text" error={!!errors?.last_name} maxLength={255} {...register('last_name')} data-testid="last-name-input" />
                        </Field>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group col-6">
                        <Field label={fixedFieldsObj?.email?.custom_name || editableTranslate('field.email')}>
                          <Input data-testid="email-input" type="email" maxLength={255} value={user?.email || ''} readOnly />
                        </Field>
                        <Field label={fixedFieldsObj?.employee_number?.custom_name || editableTranslate('field.employee_number')} className="mt-3" error={errors?.employee_number?.message}>
                          {
                            canAddEmployeeNumber
                              ? <Input data-testid="employee-number-input" type="text" error={!!errors?.employee_number} maxLength={255} {...register('employee_number')} />
                              : <Input data-testid="employee-number-input" type="text" value={user?.employeeInfo?.employee_number || ''} readOnly />
                          }
                        </Field>
                      </div>
                      <div className="form-group col-6">
                        <Field label={editableTranslate('login_users.password')} htmlFor="password" error={errors?.password?.message}>
                          <InputAffix error={!!errors?.password} suffix={<>
                            <button data-testid="password-visibility-toggle"
                              type="button"
                              className="peb-input-affix__action peb-input-affix__action--compact"
                              onClick={() => setShowPassword(!showPassword)}
                            ><i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i></button>
                            <button data-testid="randomize-password-button"
                              type="button"
                              className="peb-input-affix__action peb-input-affix__action--compact"
                              data-tooltip-id="randomize-tooltip"
                              data-tooltip-content={translate('login_users.randomize_password', `Zufallsgeneriertes Passwort`)}
                              onClick={handleRandomize}
                            ><i className={`bi bi-shuffle`}></i></button>
                          </>}>
                            <Input type={showPassword ? 'text' : 'password'} error={!!errors?.password} id="password" {...register('password')} data-testid="password-input" />
                          </InputAffix>
                        </Field>
                        <ReactTooltip id="randomize-tooltip" place="bottom" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group col-6">
                        <Field label={editableTranslate('login_users.role')}>
                          <div className="d-flex flex-wrap">
                            {
                              readonlyPermissionRoles.map(role => <ReadOnlyRoleChip key={role} roleName={role} editableTranslate={editableTranslate} />)
                            }
                            {
                              readonlyPermissionRoles.length === 0 && <span>-</span>
                            }
                          </div>
                        </Field>
                      </div>
                      <div className="form-group col-6">
                        <label className="peb-field__label">&nbsp;</label>
                        <div className="text-right">
                          <Button variant="secondary" onClick={() => setShowDeleteModal(true)} data-testid="delete-button"><i className="bi bi-trash text-normal"></i> {editableTranslate('global.delete')}</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="employee-data-card-div w-50">
                  <div className="card-setting-header d-flex justify-content-between align-items-center">
                    <span>{editableTranslate('login_users.groups', 'Gruppen')}</span>
                  </div>
                  <hr className="mt-2" />
                  <div className="card-setting-body mb-n3">
                    <div className="form-row mb-2">
                      <div className="form-group col-6">
                        <Field label={editableTranslate(!isDbModuleActive ? 'group_setting.evaluating' : 'state_employee.first_evaluator_of')}>
                          <GroupsSelector value={groupEvaluating} setter={setGroupEvaluating} role="evaluator" roleOpened={roleOpened} setRoleOpened={setRoleOpened} readOnly={false} />
                        </Field>
                      </div>
                      {
                        isDbModuleActive && <div className="form-group col-6">
                          <Field label={editableTranslate('state_employee.second_evaluator_of')}>
                            <GroupsSelector value={groupSecondaryEvaluating} setter={setGroupSecondaryEvaluating} role="secondary-evaluator" roleOpened={roleOpened} setRoleOpened={setRoleOpened} readOnly={false} />
                          </Field>
                        </div>
                      }
                      {
                        !isDbModuleActive && <div className="form-group col-6">
                          <Field label={editableTranslate('group_setting.reviewing')}>
                            <GroupsSelector value={groupReviewing} setter={setGroupReviewing} role="reviewer" roleOpened={roleOpened} setRoleOpened={setRoleOpened} readOnly={false} />
                          </Field>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        }
      </div>
      <div className="row flex-nowrap mt-3">
        <div className="col-12 d-flex justify-content-between login-users-detail">
          {
            roleOpened && <GroupSelectorDropdown
              value={roleOpened === 'evaluator' ? groupEvaluating : (roleOpened === 'secondary-evaluator' ? groupSecondaryEvaluating : groupReviewing)}
              setter={roleOpened === 'evaluator' ? setGroupEvaluating : (roleOpened === 'secondary-evaluator' ? setGroupSecondaryEvaluating : setGroupReviewing)}
              options={roleOpened === 'evaluator' ? groupEvaluatingOptions : (roleOpened === 'secondary-evaluator' ? groupSecondaryEvaluatingOptions : groupReviewingOptions)} />
          }
        </div>
      </div>

      <Modal
        open={showDeleteModal}
        danger
        loading={loadingDelete}
        title={editableTranslate('login_users.delete_confirmation_title', 'Nutzende:n löschen?')}
        cancelLabel={editableTranslate('button.cancel')}
        okLabel={editableTranslate('button.proceed')}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleUserDelete}
        testIdPrefix="login-users-delete-"
      >
        {editableTranslate('login_users.delete_warning')}
      </Modal>
    </div >
  )
}

function UserRow({ user, selectedUser, setSelectedUser, roles, groups, fetchUsers, resizedCount = null, isDbModuleActive, fixedFieldsObj = {} }) {

  const { translate } = useTranslation();

  const userRoles = user.roles || [];

  const sides = roleIconSides(user);

  const [isEditMode, setIsEditMode] = useState(false);

  const handleClick = (e) => {
    if (e.target.className.includes('bi-pencil-square')) {
      setIsEditMode(true);
    } else {
      setIsEditMode(false);
      if (user.hash_id === selectedUser?.hash_id) {
        setSelectedUser(null);
      } else {
        setSelectedUser(user);
      }
    }
  };

  return <>
    <tr data-testid={`row-${user?.hash_id}`} className={`cursor-pointer ${user.hash_id === selectedUser?.hash_id ? 'selected-employee-row' : 'table-row'}`} onClick={handleClick}>
      <td className="text-center v-middle w-1px"><i className={`open-row-icon bi bi-chevron-${user.hash_id === selectedUser?.hash_id ? 'up' : 'down'}`}></i></td>
      <td className="v-middle">
        <TextTruncate text={user.first_name} resizedCount={resizedCount} />
      </td>
      <td className="v-middle">
        <TextTruncate text={user.last_name} resizedCount={resizedCount} />
      </td>
      <td className="v-middle">
        <TextTruncate text={user?.employeeInfo?.employee_number} resizedCount={resizedCount} />
      </td>
      <td className="v-middle text-nowrap">
        <ModuleAwareRoleIcons roles={userRoles} isDbModuleActive={isDbModuleActive} translate={translate} />
        {(sides.admin || sides.group) && <span className="vr"></span>}
        <span className={`role-icon role-icon-evaluator mr-1 ${(userRoles.filter(role => role.includes('evaluator')).length > 0 && user.groups_evaluating.length > 0) ? '' : 'invisible'}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate(!isDbModuleActive ? 'roles.evaluator' : 'state_employee.first_evaluator')}>
          <img src={roleIcons['evaluator']} />
        </span>
        {
          isDbModuleActive ? <span className={`role-icon role-icon-secondary-evaluator mr-1 ${user.groups_secondary_evaluating.length > 0 ? '' : 'invisible'}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('state_employee.second_evaluator')}>
            <img src={roleIcons['evaluator']} />
          </span> : <span className={`role-icon role-icon-reviewer mr-1 ${(userRoles.filter(role => role.includes('reviewer')).length > 0 && user.groups_reviewing.length > 0) ? '' : 'invisible'}`} data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.reviewer')}>
            <img src={roleIcons['reviewer']} />
          </span>
        }
      </td>
      <td className="v-middle">
        {
          user.groups_evaluating.slice(0, 2).map((ev, i) => <span key={i} className="badge badge-pill badge-evaluator badge-wrap text-left mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate(!isDbModuleActive ? 'roles.evaluator' : 'state_employee.first_evaluator')}>{ev.name}</span>)
        }
        {
          user.groups_evaluating.length > 2 && <span className="badge badge-pill badge-evaluator badge-wrap text-left mr-1">+{user.groups_evaluating.length - 2}</span>
        }
        {
          isDbModuleActive && <>
            {
              user.groups_secondary_evaluating.slice(0, 2).map((ev, i) => <span key={i} className="badge badge-pill badge-secondary-evaluator badge-wrap text-left mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate('state_employee.second_evaluator')}>{ev.name}</span>)
            }
            {
              user.groups_secondary_evaluating.length > 2 && <span className="badge badge-pill badge-evaluator badge-wrap text-left mr-1">+{user.groups_secondary_evaluating.length - 2}</span>
            }
          </>
        }
        {
          !isDbModuleActive && <>
            {
              user.groups_reviewing.slice(0, 2).map((rv, i) => <span key={i} className="badge badge-pill badge-reviewer badge-wrap text-left mr-1" data-tooltip-id="role-tooltip" data-tooltip-content={translate('roles.reviewer')}>{rv.name}</span>)
            }
            {
              user.groups_reviewing.length > 2 && <span className="badge badge-pill badge-reviewer badge-wrap text-left mr-1">+{user.groups_reviewing.length - 2}</span>
            }
          </>
        }
      </td>
      <td style={{ minWidth: '100px' }}>
        {
          user.hash_id === selectedUser?.hash_id && (isEditMode ? '' : <i className="bi bi-pencil-square text-default cursor-pointer"></i>)
        }
      </td>
    </tr>
    {
      user.hash_id === selectedUser?.hash_id && <tr className="employee-detail-row">
        <td colSpan={7}>
          <UserDetail user={user} groups={groups} fetchUsers={fetchUsers} isEditMode={isEditMode} setIsEditMode={setIsEditMode} isDbModuleActive={isDbModuleActive} fixedFieldsObj={fixedFieldsObj} />
        </td>
      </tr>
    }
  </>;
}

function NewUserView({ user, roles, groups, fetchUsers, manual = false, fromEmployee = false, setShowNewUser = null, isDbModuleActive, fixedFieldsObj = {} }) {

  const { translate, editableTranslate } = useTranslation();

  const permissions = useSelector(getPermissions);

  const lang = useSelector(getLang);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [nameMatches, setNameMatches] = useState([]);
  const [linkConfirmation, setLinkConfirmation] = useState(null);
  const [numberIdentity, setNumberIdentity] = useState(null);

  const [groupEvaluating, setGroupEvaluating] = useState([]);
  const [groupEvaluatingOptions, setGroupEvaluatingOptions] = useState([]);

  const [groupSecondaryEvaluating, setGroupSecondaryEvaluating] = useState([]);
  const [groupSecondaryEvaluatingOptions, setGroupSecondaryEvaluatingOptions] = useState([]);

  const [groupReviewing, setGroupReviewing] = useState([]);
  const [groupReviewingOptions, setGroupReviewingOptions] = useState([]);

  const [showPassword, setShowPassword] = useState(false);

  const [roleOpened, setRoleOpened] = useState(null);

  const containerRef = useRef(null);

  const scrollToError = () => {
    if (!manual && !fromEmployee) scrollInto('.selected-employee-row', false, -150);
    else scrollInto('.login-users-new', false, -150);
  };

  const searchEmployees = useCallback(
    debounce((inputValue, callback) => {
      fetchSearchEmployees(inputValue).then(options => callback(options));
    }, 500)
    , []);

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const schema = yup.object({
    user_hash_id: yup.string().notRequired(),
    employee_number: manual
      ? yup.string().trim().required().max(255).label(fixedFieldsObj?.employee_number?.custom_name || translate('field.employee_number'))
      : yup.string().notRequired(),
    first_name: yup.string().required().label(fixedFieldsObj?.first_name?.custom_name || translate('field.first_name')),
    last_name: yup.string().required().label(fixedFieldsObj?.last_name?.custom_name || translate('field.last_name')),
    email: yup.string().email().required().label(fixedFieldsObj?.email?.custom_name || translate('field.email')),
    password: yup.string().required().label(translate('login_users.password')),
  });

  const { register, watch, control, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
  });

  const watchHashId = watch('user_hash_id');
  const watchEmail = watch('email');
  const watchEmployeeNumber = watch('employee_number');

  // The Personalnummer decides who the person is (LIX-1809): look it up while typing, so the form can
  // say whose it is before saving. The save itself is checked again by the backend.
  useEffect(() => {
    if (!manual) return;

    const number = (watchEmployeeNumber || '').trim();
    if (number === '') {
      setNumberIdentity(null);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ employee_number: number, email: (watchEmail || '').trim() });
        const { data } = await api.get(`/users/identity-check?${params.toString()}`, { meta: { background: true } });
        setNumberIdentity({ number, ...data?.data });
      } catch (err) {
        setNumberIdentity(null);
        Sentry.captureException(err);
      }
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual, watchEmployeeNumber, watchEmail]);

  const numberResolution = numberIdentity?.number === (watchEmployeeNumber || '').trim() ? numberIdentity?.resolution : null;
  const numberTarget = numberIdentity?.target;
  const linksToEmployee = ['link', 'link_email_change'].includes(numberResolution);
  const numberBlocked = ['number_login_user', 'number_archived'].includes(numberResolution);

  // The employee's names stay when the login is added to it; the email may still be changed.
  useEffect(() => {
    if (!linksToEmployee || !numberTarget) return;

    setValue('first_name', numberTarget.first_name || '');
    setValue('last_name', numberTarget.last_name || '');
    if (empty(watchEmail)) setValue('email', numberTarget.email || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linksToEmployee, numberTarget?.user_hash_id]);
  // "From employees" may replace an outdated employee email (LIX-1811); say so before saving.
  const emailChanged = fromEmployee
    && !empty(selectedEmployee?.email)
    && !empty(watchEmail)
    && watchEmail.trim().toLowerCase() !== selectedEmployee.email.toLowerCase();

  useEffect(() => {
    const groupOptions = [];

    const categories = {};
    const uncategorized = [];

    groups?.forEach(g => {
      const obj = { value: g.hash_id, label: g.name };
      const cat = g?.category ? g.category.trim().toLowerCase() : null;

      if (cat) {
        if (!categories[cat]) categories[cat] = [];

        categories[cat].push(obj);
      } else {
        uncategorized.push(obj);
      }
    });

    const keys = Object.keys(categories).sort();
    keys.forEach(key => {
      groupOptions.push({
        label: key,
        options: categories[key]
      });
    });

    groupOptions.push({
      label: translate('login_users.uncategorized'),
      uncategorized: true,
      options: uncategorized
    });

    setGroupEvaluatingOptions(groupOptions);
    setGroupSecondaryEvaluatingOptions(groupOptions);
    setGroupReviewingOptions(groupOptions);
  }, [groups]);

  const handleBlur = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
      setRoleOpened(null);
    }
  };

  const checkGroups = () => {
    const evaluatingValues = new Set(groupEvaluating.map(ge => ge.value));

    if (isDbModuleActive) {
      return groupSecondaryEvaluating.filter(gse => evaluatingValues.has(gse.value)).map(gse => gse.label);
    } else {
      return groupReviewing.filter(gr => evaluatingValues.has(gr.value)).map(gr => gr.label);
    }
  };

  // Same-name employees are only a hint: two people can share a name, so saving stays possible (LIX-1810).
  const fetchNameMatches = async (values) => {
    try {
      const params = new URLSearchParams({
        employee_number: values.employee_number || '',
        email: values.email || '',
        first_name: values.first_name || '',
        last_name: values.last_name || '',
      });
      const { data } = await api.get(`/users/identity-check?${params.toString()}`);
      return data?.data?.name_matches || [];
    } catch (err) {
      Sentry.captureException(err);
      return [];
    }
  };

  const handleSave = async (values, { confirmLink = false, skipNameCheck = false } = {}) => {
    setError(null);
    setNameMatches([]);
    setLinkConfirmation(null);

    const checkedGroups = checkGroups();
    if (checkedGroups.length > 0) {
      setError(translate(isDbModuleActive ? 'state_employee.evaluate_same_group_error' : 'login_users.error_evaluating_reviewing_group')
        .replace('$X', '<b>' + checkedGroups.join(', ') + '</b>'));
      scrollToError();
      return;
    }

    setLoading(true);

    if (manual && !skipNameCheck) {
      const matches = await fetchNameMatches(values);
      if (matches.length > 0) {
        setNameMatches(matches);
        setLoading(false);
        return;
      }
    }

    const formData = {
      evaluators: groupEvaluating.map(g => g.value),
      reviewers: groupReviewing.map(g => g.value),
      ...values
    };

    if (isDbModuleActive) {
      formData.secondary_evaluators = groupSecondaryEvaluating.map(g => g.value);
    }

    if (manual) {
      delete formData.user_hash_id;
      if (confirmLink) formData.confirm_link = true;
    }

    try {
      let result = null;
      if (!manual) {
        result = await api.put(`/users`, formData);
      } else {
        result = await api.post(`/users`, formData);
      }

      if (result.status === 200) {
        fetchUsers(false);
        toast.success(translate('form.success.saved'));
        if (manual || fromEmployee) setShowNewUser(false);
      }
    } catch (err) {
      const confirmation = err?.response?.status === 409 ? err?.response?.data?.data?.confirmation : null;
      if (manual && confirmation) {
        setLinkConfirmation(confirmation);
        setLoading(false);
        return;
      }

      let formatted = errorMessage(err, 'save login user');
      if (err?.response?.data) {
        const errData = err?.response?.data;
        if (errData?.data?.existing_email) {
          formatted = translate('message.error.email_already_exists')
            .replace('$X', '<b>' + errData?.data?.existing_email?.name + '</b>');
        } else if (errData?.data?.existing_login_user) {
          const owner = errData.data.existing_login_user;
          formatted = translate('message.error.employee_number_belongs_to_login_user', 'Diese Personalnummer ist bereits $X zugeordnet.')
            .replace('$X', '<b>' + owner.name + '</b> (' + owner.email + ')');
        } else if (errData?.data?.archived_employee) {
          formatted = translate('message.error.employee_number_archived', 'Die Personalnummer $X gehört zu einem archivierten Beschäftigtendatensatz.')
            .replace('$X', errData.data.archived_employee.employee_number);
        }
      }
      setError(formatted);
      scrollToError();
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const fetchSearchEmployees = async (input) => {
    if (input.length < 2) return [];

    try {
      const { data, status } = await api.get(`/users/no-role?q=${encodeURIComponent(input)}`);
      if (status === 200) {
        return data.data.map(u => ({
          label: u.full_name,
          value: u.hash_id,
          user: u
        }));
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch search employees'));
      scrollToError();
      Sentry.captureException(err);
    }
    return [];
  };

  const resetForm = () => {
    reset({
      user_hash_id: null,
      employee_number: '',
      first_name: '',
      last_name: '',
      email: '',
      password: ''
    });
  };

  const handleSelectEmployee = (val) => {
    if (!val) {
      setSelectedEmployee(null);
      resetForm();
      setGroupEvaluating([]);
      setGroupReviewing([]);
      setGroupSecondaryEvaluating([]);
      return;
    }

    setSelectedEmployee(val.user);

    reset({
      user_hash_id: val.user?.hash_id,
      first_name: val.user?.first_name,
      last_name: val.user?.last_name,
      email: val.user?.email,
      password: ''
    });
  };

  // Filling in that employee's number runs the normal "add the login to this employee" flow.
  const handleUseEmployee = (match) => {
    setNameMatches([]);
    setValue('employee_number', match.employee_number, { shouldValidate: true });
  };

  const handleRandomize = () => {
    const val = randomString(10);

    setValue('password', val);

    setShowPassword(true);
  };

  return <div>
    <form className="bg-white p-3 border-radius-8px" onSubmit={handleSubmit(values => handleSave(values))}>
      {
        error && <div>
          <ErrorAlert error={error} isHtml={true} onClose={() => setError(null)} />
        </div>
      }

      <div>
        {
          fromEmployee && <>
            <div className="form-row">
              <div className="form-group col-6">
                <Field label={editableTranslate('login_users.employee')}>
                  <div style={{ display: 'contents' }} data-testid="employee-select">
                    <Select
                      as={AsyncSelect}
                      noOptionsMessage={({ inputValue }) => {
                        if (inputValue.length < 2) {
                          return translate('global.search_min_2', 'Bitte geben Sie mindestens 2 Zeichen für die Suche ein');
                        } else {
                          return translate('global.no_options', 'Keine Optionen');
                        }
                      }}
                      cacheOptions
                      isClearable={true}
                      placeholder={translate('login_users.search_employee_placeholder')}
                      loadOptions={searchEmployees}
                      value={selectedEmployee ? { label: selectedEmployee.full_name, value: selectedEmployee.hash_id, user: selectedEmployee } : null}
                      onChange={handleSelectEmployee} />
                  </div>
                </Field>
              </div>
            </div>
          </>
        }

        {
          manual && <div className="form-row">
            <div className="form-group col-6">
              <Field label={fixedFieldsObj?.employee_number?.custom_name || editableTranslate('field.employee_number')} required error={errors?.employee_number?.message}>
                <Input type="text" error={!!errors?.employee_number || numberBlocked} maxLength={255} {...register('employee_number')} data-testid="employee-number-input" />
                {
                  linksToEmployee && <small className="form-text text-muted" data-testid="employee-number-hint">
                    {editableTranslate('login_users.employee_number_hint_link', 'Diese Personalnummer gehört zu {name}. Der Login wird diesem Beschäftigten zugeordnet.', { name: numberTarget?.name })}
                  </small>
                }
                {
                  numberResolution === 'number_login_user' && <div className="peb-field__error" data-testid="employee-number-hint">
                    {editableTranslate('login_users.employee_number_hint_login_user', 'Diese Personalnummer ist bereits {name} ({email}) zugeordnet.', { name: numberTarget?.name, email: numberTarget?.email })}
                  </div>
                }
                {
                  numberResolution === 'number_archived' && <div className="peb-field__error" data-testid="employee-number-hint">
                    {editableTranslate('login_users.employee_number_hint_archived', 'Diese Personalnummer gehört zu einem archivierten Beschäftigtendatensatz.')}
                  </div>
                }
              </Field>
            </div>
          </div>
        }

        {
          (watchHashId || manual) && <>
            <div className="form-row">
              <div className="form-group col-6">
                <Field label={fixedFieldsObj?.first_name?.custom_name || editableTranslate('field.first_name')} required error={errors?.first_name?.message}>
                  <Input type="text" error={!!errors?.first_name} maxLength={255} readOnly={!manual || linksToEmployee} {...register('first_name')} data-testid="first-name-input" />
                </Field>
              </div>
              <div className="form-group col-6">
                <Field label={fixedFieldsObj?.last_name?.custom_name || editableTranslate('field.last_name')} required error={errors?.last_name?.message}>
                  <Input type="text" error={!!errors?.last_name} maxLength={255} readOnly={!manual || linksToEmployee} {...register('last_name')} data-testid="last-name-input" />
                </Field>
              </div>
            </div>
            <div className="form-row">
              <div className={`form-group ${manual ? 'col-6' : 'col'}`}>
                <Field label={fixedFieldsObj?.email?.custom_name || editableTranslate('field.email')} required error={errors?.email?.message}>
                  <Input type="text" error={!!errors?.email} maxLength={255} {...register('email')} data-testid="email-input" />
                  {emailChanged && (
                    <small className="form-text text-muted" data-testid="email-change-note">
                      {editableTranslate('login_users.employee_email_change_note', 'Die E-Mail-Adresse von {name} wird von {from} zu {to} geändert.', {
                        name: selectedEmployee?.full_name,
                        from: selectedEmployee?.email,
                        to: watchEmail,
                      })}
                    </small>
                  )}
                </Field>
              </div>
              <div className="form-group col-6">
                <Field label={editableTranslate('login_users.temporary_password')} htmlFor="password" required error={errors?.password?.message}>
                  <InputAffix error={!!errors?.password} suffix={<>
                    <button data-testid="password-visibility-toggle"
                      type="button"
                      className="peb-input-affix__action peb-input-affix__action--compact"
                      onClick={() => setShowPassword(!showPassword)}
                    ><i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i></button>
                    <button data-testid="randomize-password-button"
                      type="button"
                      className="peb-input-affix__action peb-input-affix__action--compact"
                      data-tooltip-id="randomize-tooltip"
                      data-tooltip-content={translate('login_users.randomize_password', `Zufallsgeneriertes Passwort`)}
                      onClick={handleRandomize}
                    ><i className={`bi bi-shuffle`}></i></button>
                  </>}>
                    <Input type={showPassword ? 'text' : 'password'} error={!!errors?.password} id="password" {...register('password')} data-testid="password-input" />
                  </InputAffix>
                </Field>
                <ReactTooltip id="randomize-tooltip" place="bottom" />
              </div>
            </div>
          </>
        }

        <div className={`${!manual ? 'user-data-box mt-3' : ''}`} ref={containerRef} tabIndex={-1} onBlur={handleBlur}>
          <div className="form-row">
            <div className="form-group col-6 mb-2">
              <Field label={editableTranslate(!isDbModuleActive ? 'group_setting.evaluating' : 'state_employee.first_evaluator_of')}>
                <GroupsSelector value={groupEvaluating} setter={setGroupEvaluating} role="evaluator" roleOpened={roleOpened} setRoleOpened={setRoleOpened} />
              </Field>
            </div>
            {
              isDbModuleActive && <div className="form-group col-6 mb-2">
                <Field label={editableTranslate('state_employee.second_evaluator_of')}>
                  <GroupsSelector value={groupSecondaryEvaluating} setter={setGroupSecondaryEvaluating} role="secondary-evaluator" roleOpened={roleOpened} setRoleOpened={setRoleOpened} />
                </Field>
              </div>
            }
            {
              !isDbModuleActive && <div className="form-group col-6 mb-2">
                <Field label={editableTranslate('group_setting.reviewing')}>
                  <GroupsSelector value={groupReviewing} setter={setGroupReviewing} role="reviewer" roleOpened={roleOpened} setRoleOpened={setRoleOpened} />
                </Field>
              </div>
            }
          </div>
          {
            roleOpened && <GroupSelectorDropdown
              value={roleOpened === 'evaluator' ? groupEvaluating : (roleOpened === 'secondary-evaluator' ? groupSecondaryEvaluating : groupReviewing)}
              setter={roleOpened === 'evaluator' ? setGroupEvaluating : (roleOpened === 'secondary-evaluator' ? setGroupSecondaryEvaluating : setGroupReviewing)}
              options={roleOpened === 'evaluator' ? groupEvaluatingOptions : (roleOpened === 'secondary-evaluator' ? groupSecondaryEvaluatingOptions : groupReviewingOptions)} />
          }
        </div>

        {
          nameMatches.length > 0 && <Banner intent="warning" className="mt-3" data-testid="name-match-warning">
            {nameMatches.map(match => <div key={match.user_hash_id} className="d-flex align-items-center flex-wrap mb-2" data-testid="name-match">
              <span className="mr-2">
                {editableTranslate('login_users.name_match_warning', 'Es gibt bereits eine*n Beschäftigte*n mit diesem Namen (Personalnummer {employee_number}).', { employee_number: match.employee_number })}
              </span>
              {
                !match.is_login_user && <Button variant="secondary" size="sm" onClick={() => handleUseEmployee(match)} data-testid="name-match-use-employee">
                  {editableTranslate('login_users.name_match_use_employee', 'Diese*n Beschäftigte*n zum Login-User machen')}
                </Button>
              }
            </div>)}
            <Button variant="secondary" size="sm" onClick={handleSubmit(values => handleSave(values, { skipNameCheck: true }))} data-testid="name-match-continue">
              {editableTranslate('login_users.name_match_continue', 'Trotzdem neu anlegen')}
            </Button>
          </Banner>
        }

        <div className="text-center mt-4">
          <Button variant="secondary" onClick={() => setShowNewUser(false)} data-testid="cancel-button">
            {editableTranslate("button.cancel")}
          </Button>
          {
            (watchHashId || manual) && <Button type="submit" variant="primary" loading={loading} disabled={manual && numberBlocked} data-testid="save-button" className="ml-2">{editableTranslate('button.save')}</Button>
          }
        </div>
      </div>
    </form>

    <Modal
      open={!!linkConfirmation}
      loading={loading}
      title={editableTranslate('login_users.add_login_confirm_title', 'Login hinzufügen?')}
      cancelLabel={editableTranslate('button.cancel')}
      okLabel={editableTranslate('login_users.add_login_confirm_button', 'Login hinzufügen')}
      onClose={() => setLinkConfirmation(null)}
      onConfirm={handleSubmit(values => handleSave(values, { confirmLink: true, skipNameCheck: true }))}
      testIdPrefix="add-login-"
    >
      {
        linkConfirmation?.type === 'add_login_email_change'
          ? editableTranslate('login_users.add_login_email_change_confirm', 'Die E-Mail-Adresse von {name} wird von {from} zu {to} geändert. Fortfahren?', {
            name: linkConfirmation?.name,
            from: linkConfirmation?.from,
            to: linkConfirmation?.to,
          })
          : linkConfirmation?.type === 'add_login_other_number'
            ? editableTranslate('login_users.add_login_other_number_confirm', 'Diese E-Mail-Adresse gehört zu {name} (Personalnummer {employee_number}). Der Login wird diesem Beschäftigten zugeordnet; die eingegebene Personalnummer {entered_employee_number} wird nicht verwendet. Fortfahren?', {
              name: linkConfirmation?.name,
              employee_number: linkConfirmation?.employee_number,
              entered_employee_number: linkConfirmation?.entered_employee_number,
            })
            : editableTranslate('login_users.add_login_confirm', '{name} ist bereits als Beschäftigte*r angelegt (Personalnummer {employee_number}). Login hinzufügen?', {
              name: linkConfirmation?.name,
              employee_number: linkConfirmation?.employee_number,
            })
      }
    </Modal>
  </div>;
}

function NewUser({ roles, groups, fetchUsers, setShowNewUser, isDbModuleActive, fixedFieldsObj = { } }) {

  const { editableTranslate } = useTranslation();

  const [activeTab, setActiveTab] = useState('manual');

  const tabClass = (tab) => `nav-link ${activeTab === tab ? 'active' : ''}`;
  const paneClass = (tab) => `tab-pane fade ${activeTab === tab ? 'show active' : ''}`;

  return <div className="employee-detail-div login-users-new">
    <div className="bg-white p-3 rounded-sm">
      <h4 className="mb-3">{editableTranslate('login_users.new_user')}</h4>

      <nav>
        <div className="nav nav-tabs" data-testid="new-user-tabs">
          <button className={tabClass('manual')} onClick={() => setActiveTab('manual')} type="button" data-testid="new-user-tab-manual">{editableTranslate('login_users.create_manually')}</button>
          <button className={tabClass('from-employees')} onClick={() => setActiveTab('from-employees')} type="button" data-testid="new-user-tab-from-employees">{editableTranslate('login_users.from_employees')}</button>
        </div>
      </nav>
      <div className="tab-content">
        <div className={paneClass('manual')} id="manual" data-testid="new-user-pane-manual">
          <NewUserView user={null} roles={roles} groups={groups} fetchUsers={fetchUsers} manual={true} setShowNewUser={setShowNewUser} isDbModuleActive={isDbModuleActive} fixedFieldsObj={fixedFieldsObj} />
        </div>
        <div className={paneClass('from-employees')} id="from-employees" data-testid="new-user-pane-from-employees">
          <NewUserView user={null} roles={roles} groups={groups} fetchUsers={fetchUsers} fromEmployee={true} setShowNewUser={setShowNewUser} isDbModuleActive={isDbModuleActive} fixedFieldsObj={fixedFieldsObj} />
        </div>
      </div>
    </div>
  </div>;
}

export default function LoginUsers({ tabsComponent, search, setCounters }) {
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const { helps } = useOutletContext();

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const fixedFields = useSelector(getFixedFields);

  const [searchParams, setSearchParams] = useSearchParams();

  const permissions = useSelector(getPermissions);

  const groups = useSelector(getGroups);
  const roles = useSelector(getRoles);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchValue, setSearchValue] = useState(search || '');

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    perPage: 20
  });

  const [showNewUser, setShowNewUser] = useState(false);

  const [filteredRoles, setFilteredRoles] = useState(['evaluator', 'reviewer', 'admins']);

  const [showFilter, setShowFilter] = useState(false);

  const [sortCols, setSortCols] = useState([['first_name', 'asc']]);
  const [sortPinCol, setSortPinCol] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);
  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const filterContainerRef = useRef(null);

  const tabsRef = useRef(null);
  const floatingSeparatorRef = useRef(null);

  const theadRef = useRef(null);

  const tableRef = useRef(null);

  const fixedFieldsObj = useMemo(() => {
    if (!fixedFields) return {};

    const obj = {};
    for (const f of fixedFields) {
      obj[f.slug] = f;
    }
    return obj;
  }, [fixedFields]);

  const objSortCols = useMemo(() => {
    const obj = {};

    for (const sc of sortCols) {
      obj[sc[0]] = sc[1];
    }

    return obj;
  }, [sortCols]);

  useEffect(() => {
    if (isDbModuleActive) setFilteredRoles(['evaluator', 'secondary_evaluator', 'admins']);
  }, [isDbModuleActive]);

  useEffect(() => {
    dispatch(fetchGroups());
    if (!roles) dispatch(fetchRoles(true));

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const tabsEl = tabsRef.current;
    if (!tabsEl) return;

    const floatingEl = floatingSeparatorRef.current;
    if (!floatingEl) return;

    const parentEl = theadEl.parentElement;
    const tabsParentEl = tabsEl.parentElement;

    const handleScrollTable = () => {
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const tabsParentCoordinates = tabsParentEl.getBoundingClientRect();
      if (tabsParentCoordinates.y < navHeight) {
        tabsEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7 - 21}px, 0)`;
        theadEl.style.transform = `translate3d(0, ${-coordinates.y + navHeight + tabsEl.offsetHeight + 28}px, 0)`;

        floatingEl.style.display = 'block';
        floatingEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight - 21}px, 0)`;
        floatingEl.style.height = (tabsEl.offsetHeight + theadEl.offsetHeight + 42) + 'px';
      } else {
        tabsEl.style.transform = 'translate3d(0,0,0)';
        theadEl.style.transform = 'translate3d(0,0,0)';

        floatingEl.style.display = 'hidden';
        floatingEl.style.transform = 'translate3d(0,0,0)';
        floatingEl.style.height = '.5rem';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, []);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [showFilter]);

  const fetchUsers = async (showLoading = true, page = null) => {
    let searchQuery = searchParams.get('search');
    const staffId = deeplinkStaffId(searchParams);
    const userId = deeplinkUserId(searchParams);
    const isDeeplink = !!(staffId || userId);

    if (filteredRoles.length < 1 && !searchQuery && !isDeeplink) {
      if (users.length > 0) setCounters(prev => ({ ...prev, login_users: 0 }));
      setUsers([]);
      setPagination({
        page: 1,
        totalRows: 0,
        totalPages: 1,
        perPage: 20
      });
      return;
    }

    if (!page) page = pagination.page;

    setError(null);
    if (showLoading) setLoading(true);
    try {
      const queryRoles = [];
      for (const item of filteredRoles) {
        if (['evaluator', 'secondary_evaluator', 'reviewer'].includes(item)) queryRoles.push(item);
        else if (item === 'admins') queryRoles.push(...visiblePermissionRoleNames(isDbModuleActive));
      }
      let url = `/users?include=employeeInfo&limit=${pagination.perPage}&page=${page}&roles=${(filteredRoles.length === 3 || searchQuery || isDeeplink) ? 'all' : queryRoles.join(',')}`;

      if (searchQuery) url += `&q=${encodeURIComponent(searchQuery)}`;
      if (staffId) url += `&staff_id=${encodeURIComponent(staffId)}`;
      else if (userId) url += `&user_id=${encodeURIComponent(userId)}`;

      if (sortCols && sortCols.length > 0) {
        url += `&sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }

      const { data, status } = await api.get(url);
      if (status === 200) {
        setUsers(data.data);
        if (userId && !searchQuery) setSearchValue(data.data[0]?.email || '');
        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: +temp.total_pages, perPage: +temp.per_page });
        setCounters(prev => ({ ...prev, login_users: +temp.total }));
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch groups'));
      setCounters(prev => ({ ...prev, login_users: 0 }));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, pagination.perPage, filteredRoles, sortCols]);

  useEffect(() => {
    fetchUsers(true, 1);

    setSearchValue(deeplinkSearchValue(searchParams));
  }, [searchParams]);

  const minColWidth = {
    roles: 150,
    groups: 200,
    action: 100
  };

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 200,
    roles: 150,
    groups: 600
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'login_users', true, minColWidth, maxColWidth, setResizedCount);
  }, [users, triggerColWidth, showFilter, selectedUser]);

  const handleFilterBlur = (e) => {
    if (filterContainerRef.current && !filterContainerRef.current.contains(e.relatedTarget)) {
      setShowFilter(false);
    }
  };

  const handleCheckFilteredRoles = (role, checked) => {
    if (checked) {
      setFilteredRoles([...filteredRoles, role]);
    } else {
      setFilteredRoles(filteredRoles.filter(r => r !== role));
    }
  };

  const handleSearchFilter = (e) => {
    e?.preventDefault();
    setSearchParams({
      tab: 'login_users',
      search: searchValue
    });
  };

  const handleFilter = (e) => {
    e?.currentTarget?.blur();
    setShowFilter(!showFilter);
  };

  const handleResetFilter = () => {
    setFilteredRoles(['evaluator', 'reviewer', 'admins']);
    setShowFilter(false);
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field, 'asc']);
    }
    setSortCols(newSortCols);
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field) {
      setSortPinCol(null);
      setSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field);
      setSortCols(sortCols.filter(sc => sc[0] === field));
    }
  };

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const handleNewUser = () => {
    setShowNewUser(!showNewUser);
    scrollToTop();
  };

  const getLabel = (key, fallback) => {
    const sKey = key.replaceAll('.', '-');
    const help = helps && helps.filter(h => h.key === sKey)[0] || null;

    return <>
      {editableTranslate(key, fallback)}
      {help && helpTextIcon(sKey, help.short_text, 'text-normal')}
    </>
  };

  return (
    <>
      <div className="row flex-nowrap">
        <div className={`col-lg ${showFilter ? 'col-md-9 section-col-right' : ''} min-height-after-navbar`}>
          <div className="bg-white w-100 p-4 content-card h-100">
            <div className="position-relative">
              <div ref={floatingSeparatorRef} className="floating-separator"></div>
            </div>
            <div ref={tabsRef} className="d-flex justify-content-between align-items-center border-bottom employee-management-tabs-div mb-4">
              {tabsComponent}
              <div className="position-relative">
                <div className="d-flex align-items-center">
                  <form className="form-inline" onSubmit={handleSearchFilter}>
                    <InputAffix className={searchValue ? 'bg-red-dark-soft' : ''} prefix={<i className="bi bi-search mr-2"></i>}>
                      <Input data-testid="search-input" type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} autoComplete="false" placeholder={translate('form.search')} maxLength={255} />
                    </InputAffix>
                  </form>
                  <div className="position-relative ml-3">
                    <Button variant="secondary" onClick={handleFilter} data-testid="filter-button" className="shadow-none"><i className="bi bi-filter"></i> {editableTranslate('form.filter')}</Button>
                  </div>
                  {
                    hasPermissionTo('settings.login_users.create', permissions) && <Button data-testid="add-button" variant="secondary" className="shadow-none ml-3" onClick={handleNewUser}>
                      <i className="bi bi-person-plus"></i> {editableTranslate('employee_management.add_user', 'Hinzufügen')}
                    </Button>
                  }
                </div>
                <div className="employee-filters-separator"></div>
              </div>
            </div>
            <div className={''}>
              {
                error && <div>
                  <ErrorAlert error={error} onClose={() => setError(null)} />
                </div>
              }

              <div className="row mt-4">
                <div className={'col'}>
                  <div className="table-responsive has-custom-scrollbar">
                    <table ref={tableRef} className="table common-table login-users-table" data-testid="users-table">
                      <thead ref={theadRef} className="sticky-top">
                        <tr>
                          <th className="text-center w-1px"></th>
                          <th scope="col" className="cursor-pointer">
                            <span className="float-right floating-icons">
                              <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['first_name'] ? (objSortCols['first_name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('first_name')} data-testid="sort-first-name" />
                              {
                                objSortCols['first_name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'first_name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('first_name')} data-testid="sort-pin-first-name" />
                              }
                            </span>
                            <span data-testid="sort-first-name-label" className="th-span" onClick={() => handleChangeSortCol('first_name')}>
                              {fixedFieldsObj?.first_name?.custom_name || editableTranslate('field.first_name')}
                              <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                              <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                            </span>
                            <div className="th-resize-div" data-field={'first_name'}></div>
                          </th>
                          <th scope="col" className="cursor-pointer">
                            <span className="float-right floating-icons">
                              <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['last_name'] ? (objSortCols['last_name'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('last_name')} data-testid="sort-last-name" />
                              {
                                objSortCols['last_name'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'last_name' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('last_name')} data-testid="sort-pin-last-name" />
                              }
                            </span>
                            <span data-testid="sort-last-name-label" className="th-span" onClick={() => handleChangeSortCol('last_name')}>
                              {fixedFieldsObj?.last_name?.custom_name || editableTranslate('field.last_name')}
                              <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                              <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                            </span>
                            <div className="th-resize-div" data-field={'last_name'}></div>
                          </th>
                          <th scope="col" className="cursor-pointer">
                            <span className="float-right floating-icons">
                              <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['employee_number'] ? (objSortCols['employee_number'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('employee_number')} data-testid="sort-employee-number" />
                              {
                                objSortCols['employee_number'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'employee_number' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('employee_number')} data-testid="sort-pin-employee-number" />
                              }
                            </span>
                            <span data-testid="sort-employee-number-label" className="th-span" onClick={() => handleChangeSortCol('employee_number')}>
                              {fixedFieldsObj?.employee_number?.custom_name || editableTranslate('field.employee_number')}
                              <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                              <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                            </span>
                            <div className="th-resize-div" data-field={'employee_number'}></div>
                          </th>
                          <th scope="col" className="cursor-pointer">
                            <span className="float-right floating-icons">
                              <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['roles'] ? (objSortCols['roles'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('roles')} data-testid="sort-roles" />
                              {
                                objSortCols['roles'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'roles' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('roles')} data-testid="sort-pin-roles" />
                              }
                            </span>
                            <span data-testid="sort-roles-label" className="th-span" onClick={() => handleChangeSortCol('roles')}>
                              {getLabel('field.roles', translate('global.menu.settings.roles'))}
                              <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                              <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                            </span>
                            <div className="th-resize-div" data-field={'roles'}></div>
                          </th>
                          <th scope="col" className="cursor-pointer">
                            <span className="float-right floating-icons">
                              <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${objSortCols['groups'] ? (objSortCols['groups'] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol('groups')} data-testid="sort-groups" />
                              {
                                objSortCols['groups'] && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === 'groups' ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol('groups')} data-testid="sort-pin-groups" />
                              }
                            </span>
                            <span data-testid="sort-groups-label" className="th-span" onClick={() => handleChangeSortCol('groups')}>
                              {getLabel('field.groups', translate('global.menu.settings.groups'))}
                              <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                              <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                            </span>
                            <div className="th-resize-div" data-field={'groups'}></div>
                          </th>
                          <th style={{ minWidth: '100px' }}>
                            <div className="th-resize-div" data-field={'action'}></div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          showNewUser && <tr className="employee-detail-row">
                            <td colSpan={7}>
                              <NewUser roles={roles} groups={groups} fetchUsers={fetchUsers} setShowNewUser={setShowNewUser} isDbModuleActive={isDbModuleActive} fixedFieldsObj={fixedFieldsObj} />
                            </td>
                          </tr>
                        }
                        {
                          !loading && users.length > 0 && users.map((user, index) => <UserRow key={index} user={user} selectedUser={selectedUser} setSelectedUser={setSelectedUser} roles={roles} groups={groups} fetchUsers={fetchUsers} resizedCount={resizedCount} isDbModuleActive={isDbModuleActive} fixedFieldsObj={fixedFieldsObj} />)
                        }
                        {
                          loading && <tr>
                            <td colSpan={7}>
                              <div className="d-flex justify-content-center" data-testid="loading">
                                <div className="spinner-border" role="status">
                                  <span className="sr-only">Wird geladen...</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        }
                        {
                          !loading && Array.isArray(users) && users.length === 0 && <tr>
                            <td colSpan={7} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                    <ReactTooltip id="role-tooltip" place="bottom" />
                  </div>
                  <TableScrollbar tableRef={tableRef} data={users} trigger={resizedCount} />
                  <ListPagination
                    page={pagination.page}
                    perPage={pagination.perPage}
                    totalPages={pagination.totalPages}
                    totalRows={pagination.totalRows}
                    setPage={(page) => setPagination({ ...pagination, page })}
                    setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
                    className="mt-4"
                  />
                </div>
              </div>

              <ReactTooltip id="last-login-tooltip" content={translate('login_users.last_login', 'Letzte Anmeldung')} place="bottom" />
            </div>
          </div>
        </div>

        {
          showFilter && <div className="col-lg-auto col-md-3 section-col-left min-height-after-navbar">
            <div className="bg-white w-100 p-4 content-card h-100">
              <div className="side-header">
                <h5 className="m-0 font-weight-bold">{editableTranslate('form.filter')}</h5>
                <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={() => setShowFilter(false)} data-testid="close-button">
                  <i className="bi bi-x-lg"></i>
                </span>
              </div>

              <div>
                {/* <i className="bi bi-x-lg" onClick={handleResetFilter}></i> */}

                <label className="font-weight-bold">{editableTranslate('form.quick_filters')}</label>

                <div className="form-check mb-2 mt-0">
                  <label className="form-check-label">
                    <input className={`form-check-input`} type="checkbox" checked={filteredRoles.includes('evaluator')} onChange={e => handleCheckFilteredRoles('evaluator', e.target.checked)} data-testid="quick-filter-evaluator" />
                    <span className="ml-1">{editableTranslate(!isDbModuleActive ? 'roles.evaluator' : 'state_employee.first_evaluator')}</span>
                  </label>
                </div>
                <div className="form-check mb-2 mt-0">
                  <label className="form-check-label">
                    <input className={`form-check-input`} type="checkbox" checked={filteredRoles.includes(!isDbModuleActive ? 'reviewer' : 'secondary_evaluator')} onChange={e => handleCheckFilteredRoles(!isDbModuleActive ? 'reviewer' : 'secondary_evaluator', e.target.checked)} data-testid="quick-filter-reviewer" />
                    <span className="ml-1">{editableTranslate(!isDbModuleActive ? 'roles.reviewer' : 'state_employee.second_evaluator')}</span>
                  </label>
                </div>
                <div className="form-check mb-2 mt-0">
                  <label className="form-check-label">
                    <input className={`form-check-input`} type="checkbox" checked={filteredRoles.includes('admins')} onChange={e => handleCheckFilteredRoles('admins', e.target.checked)} data-testid="quick-filter-admins" />
                    <span className="ml-1">{editableTranslate('roles.type.admin')}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </>
  );
}
